import {AxisFactoryInterface} from "../types/axis-factory.interface";
import {Axis} from "../axis";
import {AxisConfiguration} from "../types/axis-configuration.interface";
import {AxisDataService} from "../types/axis-data-service.interface";

/**
 * Factory class for creating instances of Axis with a specific configuration
 * and data service. This class implements the AxisFactoryInterface and provides
 * a method to create new Axis objects.
 *
 * @template TValue - The type of the value managed by the axis.
 * @template TPrimitive - The primitive type mapped to TValue (defaults to TValue).
 */
export class AxisFactory<TValue, TPrimitive extends PropertyKey = TValue & PropertyKey> implements AxisFactoryInterface<TValue, TPrimitive> {

    /**
     * Creates a new instance of Axis with the given parameters.
     *
     * @param {TValue} startCursor - The starting position or value for the axis.
     * @param {AxisConfiguration<TValue, TPrimitive>} axisConfiguration - The configuration object defining axis-specific properties, such as axis ID and value converter.
     * @param {AxisDataService<TValue>} axisDataService - The service responsible for handling data operations for the axis.
     * @return {Axis<TValue, TPrimitive>} The newly created Axis object.
     */
    create(startCursor: TValue, axisConfiguration: AxisConfiguration<TValue, TPrimitive>, axisDataService: AxisDataService<TValue>): Axis<TValue, TPrimitive> {

        return new Axis<TValue, TPrimitive>(
            axisConfiguration.axisId,
            startCursor,
            axisDataService,
            axisConfiguration.converter
        );
    }
}